import * as PIXI from "pixi.js";
import gsap from "gsap";
import { GameAssets } from "src/shared/util/GameAssets";
import { soundManager } from "src/shared/UI/SoundManager";
import { SoundInstance } from "src/shared/UI/SoundManagerUtils";

export class BonusWin extends PIXI.Container {
    private sprite: PIXI.Sprite;
    private winLayer: PIXI.Graphics;
    private titleText: PIXI.Text;
    private contentText: PIXI.Text;
    private content2Text: PIXI.Text;
    private fsAmountText: PIXI.BitmapText;
    private continueText: PIXI.Text;
    private interval!: NodeJS.Timeout;

    private bonusEndEvent: CustomEvent = new CustomEvent("BONUS_END");

    private totalWinPopupSound: SoundInstance | undefined = undefined;

    constructor() {
        super();

        this.winLayer = new PIXI.Graphics().rect(-2000, -2000,
            4000, 4000).fill(0x083c42);
        this.winLayer.alpha = 0.7;
        // Create gradient fill
        const fill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },  // Start at top
            end: { x: 0, y: 1 },    // End at bottom
            colorStops: [
                { offset: 0, color: '#ebeb24' },
                { offset: 1, color: '#e7a223' }
            ],
        });

        this.sprite = new PIXI.Sprite(GameAssets.textures.gameAtlas.textures["popup.webp"]);
        this.sprite.anchor.set(0.5);
        this.sprite.position.set(0, 0);

        this.titleText = new PIXI.Text({
            text: 'Congratulations!',
            style: {
                fontSize: 100,
                fontFamily: "TextFont",
                fill: { fill },
                fontWeight: "bold",
                stroke: {
                    color: 0x000000,
                    width: 4,
                }
            }
        });
        this.titleText.anchor.set(0.5);
        this.titleText.position.set(0, -250);

        this.contentText = new PIXI.Text({
            text: 'You have won',
            style: {
                fontSize: 80,
                wordWrap: true,             // Enable word wrapping
                wordWrapWidth: 750,         // Set the maximum width for wrapping
                align: 'center',
                fontFamily: "TextFont",
                fill: { fill },
                fontWeight: "bold",
                stroke: {
                    color: 0x000000,
                    width: 4,
                }
            }
        });
        this.contentText.anchor.set(0.5);
        this.contentText.position.set(0, -120);

        this.fsAmountText = new PIXI.BitmapText({
            text: '5',
            style: {
                fontSize: 110,
                fontFamily: "winFont",
            }
        });
        this.fsAmountText.anchor.set(0.5);
        this.fsAmountText.position.set(0, 55);

        this.content2Text = new PIXI.Text({
            text: 'in Free Spins',
            style: {
                fontSize: 80,
                wordWrap: true,             // Enable word wrapping
                wordWrapWidth: 750,         // Set the maximum width for wrapping
                align: 'center',
                fontFamily: "TextFont",
                fill: { fill },
                fontWeight: "bold",
                stroke: {
                    color: 0x000000,
                    width: 4,
                }
            }
        });
        this.content2Text.anchor.set(0.5);
        this.content2Text.position.set(0, 200);

        this.continueText = new PIXI.Text({
            text: 'Click anywhere to continue.',
            style: {
                fontSize: 50,
                fontFamily: "TextFont",
                fill: "0xffffff",
                fontWeight: "bold",
                stroke: {
                    color: 0x000000,
                    width: 4,
                }
            }
        });
        this.continueText.anchor.set(0.5);
        this.continueText.position.set(0, 300);
        gsap.to(this.continueText.scale, {
            x: 1.05,         // Scale up to 1.2x
            y: 1.05,         // Scale up to 1.2x
            duration: 0.5,  // Duration of the tween
            yoyo: true,     // Tween back to the original scale
            repeat: -1,     // Infinite loop
            ease: "sine.inOut" // Smooth easing
        });

        this.sprite.addChild(this.titleText);
        this.sprite.addChild(this.contentText);
        this.sprite.addChild(this.fsAmountText);
        this.sprite.addChild(this.content2Text);
        this.sprite.addChild(this.continueText);

        this.addChild(this.winLayer);
        this.addChild(this.sprite);
    }

    public async show(totalWin: number) {
        return new Promise<void>(async (resolve) => {
            if (totalWin === 0) {
                this.titleText.text = '';
            }
            this.sprite.y = -3000;
            this.fsAmountText.text = totalWin.toString();
            this.visible = true;
            this.winLayer.alpha = 0;

            this.totalWinPopupSound = await soundManager.playSound("totalWinPopup");

            gsap.to(this.winLayer, {
                alpha: 0.7,             // Target position
                duration: 0.5,    // Animation duration (adjust as needed)
            });
            // Tween the sprite to y = 0 with back-out easing
            gsap.to(this.sprite, {
                y: 0,             // Target position
                duration: 0.5,    // Animation duration (adjust as needed)
                ease: "back.out(1.7)", // Back-out easing with overshoot factor (adjustable)
                onComplete: resolve
            });

        })
    }

    public async close() {
        return new Promise<void>((resolve) => {
            gsap.to(this.winLayer, {
                alpha: 0,             // Target position
                duration: 0.5,    // Animation duration (adjust as needed)
            });
            // Tween the sprite to y = 0 with back-out easing
            gsap.to(this.sprite, {
                y: 3000,             // Target position
                duration: 0.5,    // Animation duration (adjust as needed)
                ease: "back.in(1.7)", // Back-out easing with overshoot factor (adjustable)
                onComplete: () => {
                    clearInterval(this.interval);
                    this.visible = false;
                    this.emit('close');

                    if (this.totalWinPopupSound) {
                        soundManager.stopSoundInstance(this.totalWinPopupSound);
                        this.totalWinPopupSound = undefined;
                    }
                    resolve();
                }
            });
            document.dispatchEvent(this.bonusEndEvent);
        })
    }
}
